import React from 'react';
import { motion } from 'framer-motion';

/**
 * Pulsating Lantern Loading Component
 * Shows while waiting for AI response
 */
const LanternLoader = ({ message = "Receiving light...", size = "default" }) => {
  const sizes = {
    small: { width: 40, height: 60 },
    default: { width: 64, height: 96 },
    large: { width: 96, height: 144 },
  };

  const { width, height } = sizes[size] || sizes.default;

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="relative">
        {/* Outer glow ring */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            width: width * 2,
            height: width * 2,
            left: -width / 2,
            top: -width / 4,
            background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Lantern SVG */}
        <motion.svg
          width={width}
          height={height}
          viewBox="0 0 64 96"
          className="relative z-10"
          animate={{
            y: [0, -5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {/* Hook */}
          <motion.path
            d="M 32 0 C 32 0 32 8 32 12"
            stroke="#DAA520"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          
          {/* Top ring */}
          <circle cx="32" cy="14" r="4" fill="#DAA520" />
          
          {/* Top cap */}
          <rect x="20" y="16" width="24" height="6" fill="#DAA520" rx="2" />
          
          {/* Body frame */}
          <rect 
            x="16" 
            y="22" 
            width="32" 
            height="52" 
            fill="none" 
            stroke="#FFD700" 
            strokeWidth="3" 
            rx="4" 
          />
          
          {/* Glass panels with glow */}
          <motion.rect
            x="20"
            y="26"
            width="24"
            height="44"
            fill="rgba(255, 215, 0, 0.15)"
            rx="2"
            animate={{
              fill: [
                "rgba(255, 215, 0, 0.15)",
                "rgba(255, 215, 0, 0.35)",
                "rgba(255, 215, 0, 0.15)",
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Vertical bars */}
          <line x1="32" y1="26" x2="32" y2="70" stroke="#DAA520" strokeWidth="1" opacity="0.5" />
          <line x1="26" y1="26" x2="26" y2="70" stroke="#DAA520" strokeWidth="1" opacity="0.3" />
          <line x1="38" y1="26" x2="38" y2="70" stroke="#DAA520" strokeWidth="1" opacity="0.3" />
          
          {/* Flame */}
          <motion.ellipse
            cx="32"
            cy="48"
            fill="#FFD700"
            animate={{
              ry: [10, 14, 8, 12, 10],
              rx: [6, 7, 5, 6, 6],
              opacity: [1, 0.9, 1, 0.85, 1],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Inner flame */}
          <motion.ellipse
            cx="32"
            cy="46"
            fill="#FFE55C"
            animate={{
              ry: [5, 7, 4, 6, 5],
              rx: [3, 4, 2, 3, 3],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Flame tip */}
          <motion.ellipse
            cx="32"
            cy="42"
            rx="1.5"
            fill="#FFFFFF"
            animate={{
              ry: [2, 3, 1.5, 2.5, 2],
              opacity: [1, 0.8, 1, 0.9, 1],
            }}
            transition={{
              duration: 0.4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Bottom cap */}
          <rect x="20" y="74" width="24" height="6" fill="#DAA520" rx="2" />
          
          {/* Bottom ring */}
          <rect x="24" y="80" width="16" height="4" fill="#B8860B" rx="1" />
        </motion.svg>

        {/* Light rays */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
          <motion.div
            key={angle}
            className="absolute"
            style={{
              width: 2,
              height: 20,
              background: 'linear-gradient(to bottom, rgba(255, 215, 0, 0.6), transparent)',
              left: '50%',
              top: '40%',
              transformOrigin: 'center bottom',
              transform: `rotate(${angle}deg) translateX(-50%)`,
            }}
            animate={{
              opacity: [0, 0.6, 0],
              scaleY: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.15,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Loading text */}
      <motion.p
        className="text-primary font-medium text-center"
        animate={{
          opacity: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {message}
      </motion.p>

      {/* Animated dots */}
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-primary"
            animate={{
              y: [0, -8, 0],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.15,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LanternLoader;
